/**
 * Seamless, organic glow-wave geometry for the mobile Lumio overlay.
 *
 * The Figma concept (node 8467:18951) paints the lumio glows as soft, blurred
 * wave-shaped blobs that drift behind the header and footer. We RECREATE them as
 * SVG paths (rather than ship the flattened bitmaps) so they can scroll forever
 * without a seam — and so they read as a real, "variable" wave, not a single sine.
 *
 * Seamless-loop contract
 * ----------------------
 * Each path is built over FOUR periods (`width = 4 · period`) of a function that is
 * exactly periodic in `period` (every harmonic completes an integer number of
 * cycles per period). The SVG is rendered at 400% of its container with
 * `preserveAspectRatio="none"`, so one `period` of viewBox maps to one container
 * width on screen. The element is then animated between `-25%` and `-50%` of its
 * own width (a one-period travel), which keeps the visible 1×-wide window in the
 * INTERIOR (periods 1→3) at all times — never within a period of the element's
 * edges. That matters because `filter: blur()` fades the element's left/right
 * edges; with only two periods + a `0 → -50%` translate those faded edges scroll
 * into view at the loop boundary and read as a "snap". Four periods keeps the
 * faded edges permanently off-screen, so the blurred loop is truly seamless.
 *
 * "Not a simple sine"
 * -------------------
 * Every wave sums 3–4 integer harmonics with hand-picked amplitudes and phases,
 * giving an asymmetric, lumpy crest (one tall swell + secondary ripples) that
 * looks like an organic light bloom once blurred — while staying perfectly
 * periodic. All coefficients are fixed (no Math.random), so the geometry is
 * deterministic and stable across renders/SSR.
 */

export type Harmonic = {
  /** Cycles completed across one `period` — MUST be an integer (keeps it seamless). */
  k: number;
  /** Relative weight of this harmonic (normalised against the others). */
  amp: number;
  /** Phase offset in radians — shifts where this harmonic's crest lands. */
  phase: number;
};

export type WaveSpec = {
  /** Repeat width in viewBox units (one container width maps to this on screen). */
  period: number;
  /** viewBox height in units. */
  height: number;
  /** Mean line of the wave (viewBox units from the top). */
  baseline: number;
  /** Peak deviation of the crest from the baseline (viewBox units). */
  amplitude: number;
  /** The harmonic stack — summed and normalised to `amplitude`. */
  harmonics: Harmonic[];
  /** `down` fills the area below the wave (crest points up, glow hugs the bottom);
   *  `up` fills above it (crest points down, glow hugs the top). */
  fill: 'up' | 'down';
};

export type WavePath = {
  /** The SVG path `d`, built over `REPEATS` periods. */
  d: string;
  /** viewBox width (= REPEATS · period). */
  width: number;
  /** viewBox height. */
  height: number;
};

const TAU = Math.PI * 2;
/** Samples per period — dense enough that the blur never reveals facets. */
const STEPS_PER_PERIOD = 96;
/** Periods drawn per tile — 4 so the scrolled, blurred interior never exposes the
 * element's faded edges (see the loop contract above). */
const REPEATS = 4;

/**
 * Build a seamless, filled wave path from a spec. See the module header for the
 * loop contract; the short version: the curve is periodic in `period`, drawn over
 * `2 · period`, and closes flush at both ends so the tile butts against itself.
 */
export const buildWavePath = (spec: WaveSpec, repeats: number = REPEATS): WavePath => {
  const { period, height, baseline, amplitude, harmonics, fill } = spec;
  const width = period * repeats;
  const totalSteps = STEPS_PER_PERIOD * repeats;
  const norm = harmonics.reduce((sum, h) => sum + Math.abs(h.amp), 0) || 1;

  const yAt = (x: number): number => {
    let sum = 0;
    for (const h of harmonics) sum += h.amp * Math.sin((TAU * h.k * x) / period + h.phase);
    return baseline - (amplitude * sum) / norm;
  };

  let d = `M0 ${yAt(0).toFixed(2)}`;
  for (let i = 1; i <= totalSteps; i += 1) {
    const x = (width * i) / totalSteps;
    d += ` L${x.toFixed(2)} ${yAt(x).toFixed(2)}`;
  }
  // Close to the fill edge so the band is a solid shape the gradient can paint.
  d += fill === 'down' ? ` L${width} ${height} L0 ${height} Z` : ` L${width} 0 L0 0 Z`;

  return { d, width, height };
};

/* ── The concrete waves ──────────────────────────────────────────────────────
 * viewBox units are arbitrary (the layer stretches them); we use period 600 ×
 * height 300 throughout so the specs read consistently. The composition mirrors
 * the concept: TOP = a cyan band hugging the top edge + a magenta swell; BOTTOM =
 * a violet/indigo base, a magenta crest, and a cyan swell in the corner. */

const P = 600;
const H = 300;

/** Top glows hang from the top edge (crest points down → `fill: 'up'`). */
const TOP_SPECS: Record<'cyan' | 'magenta', WaveSpec> = {
  cyan: {
    period: P,
    height: H,
    baseline: 150,
    amplitude: 64,
    fill: 'up',
    harmonics: [
      { k: 1, amp: 1, phase: 0.4 },
      { k: 2, amp: 0.34, phase: 1.9 },
      { k: 3, amp: 0.16, phase: 4.1 },
    ],
  },
  magenta: {
    period: P,
    height: H,
    baseline: 120,
    amplitude: 92,
    fill: 'up',
    harmonics: [
      { k: 1, amp: 1, phase: 2.7 },
      { k: 2, amp: 0.28, phase: 0.6 },
      { k: 3, amp: 0.12, phase: 3.3 },
    ],
  },
};

/** Bottom glows rise from the bottom edge (crest points up → `fill: 'down'`). */
const BOTTOM_SPECS: Record<'violet' | 'magenta' | 'cyan', WaveSpec> = {
  violet: {
    period: P,
    height: H,
    baseline: 158,
    amplitude: 52,
    fill: 'down',
    harmonics: [
      { k: 1, amp: 1, phase: 1.1 },
      { k: 2, amp: 0.3, phase: 3.0 },
      { k: 4, amp: 0.12, phase: 0.2 },
    ],
  },
  magenta: {
    period: P,
    height: H,
    baseline: 126,
    amplitude: 94,
    fill: 'down',
    harmonics: [
      { k: 1, amp: 1, phase: 3.9 },
      { k: 2, amp: 0.26, phase: 1.4 },
      { k: 3, amp: 0.14, phase: 5.0 },
    ],
  },
  cyan: {
    period: P,
    height: H,
    baseline: 168,
    amplitude: 74,
    fill: 'down',
    harmonics: [
      { k: 1, amp: 1, phase: 5.6 },
      { k: 2, amp: 0.32, phase: 2.2 },
      { k: 3, amp: 0.15, phase: 0.8 },
    ],
  },
};

/* ── Single-period tiles for the `background-position`-drift technique ───────────
 * Safari drops the huge GPU layer a 400%-wide, transform-animated, blurred element
 * needs (the topmost layer blinks out). The drift is now done by scrolling a TILED
 * background-image on a normal-width, non-promoted element (no will-change, no
 * translateZ, no transform). A SINGLE period tiles seamlessly because the wave is
 * exactly periodic in `period` (y(0) === y(period)), so `background-repeat: repeat-x`
 * + a one-tile `background-position` scroll loops with no seam. */
export const TOP_WAVE_TILES = {
  cyan: buildWavePath(TOP_SPECS.cyan, 1),
  magenta: buildWavePath(TOP_SPECS.magenta, 1),
} as const;

export const BOTTOM_WAVE_TILES = {
  violet: buildWavePath(BOTTOM_SPECS.violet, 1),
  magenta: buildWavePath(BOTTOM_SPECS.magenta, 1),
  cyan: buildWavePath(BOTTOM_SPECS.cyan, 1),
} as const;

/**
 * Build a tiling background-image (data-URI SVG) for one glow-wave layer.
 *
 * `color` MUST be a RAW hex string, not a `var(--ows-…)` token: a data-URI SVG is an
 * isolated document, so the page's CSS custom properties do NOT cascade into it and a
 * var() fill would render nothing. Pass the raw value from `theme/tokens.raw`
 * (`LUMIO_MOBILE.glowViolet` etc.). The vertical alpha gradient reproduces the old
 * inline-SVG fill: `down` hugs the bottom (glow rises), `up` hugs the top.
 */
export const waveTileImage = (tile: WavePath, color: string, facing: 'up' | 'down' = 'down'): string => {
  const stops =
    facing === 'down'
      ? `<stop offset='22%' stop-color='${color}' stop-opacity='0'/><stop offset='52%' stop-color='${color}' stop-opacity='0.32'/><stop offset='100%' stop-color='${color}' stop-opacity='0.85'/>`
      : `<stop offset='0%' stop-color='${color}' stop-opacity='0.85'/><stop offset='52%' stop-color='${color}' stop-opacity='0.28'/><stop offset='78%' stop-color='${color}' stop-opacity='0'/>`;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${tile.width} ${tile.height}' preserveAspectRatio='none'><defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'>${stops}</linearGradient></defs><path d='${tile.d}' fill='url(#g)'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
